import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useReducer } from 'react';
import { ConfirmationModal, Typography } from 'seagull';
import { Checkmark, CloseCross } from 'seagull/icons';
import { useOverlayContext } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import pluralize from '~/utils/pluralize';
import { useOnMount } from '../useOnMount';
import styles from './useBulkActionModal.module.scss';
function reducer(state, action) {
    switch(action.type){
        case 'succeeded':
            return _object_spread_props(_object_spread({}, state), {
                succeeded: state.succeeded + 1
            });
        case 'failed':
            return _object_spread_props(_object_spread({}, state), {
                failed: state.failed.add(action.failedId),
                errors: action.error != null ? state.errors.concat(action.error) : state.errors
            });
    }
}
function BulkActionModal(props) {
    const { t: commonTexts } = useLocaleKeys('common');
    const { bulkItemIds, progressTitle, progressAction = 'Sending', actionName, itemName, errorExtra, dismissModal, canProcessItem, processSingleItem, getErrorText } = props;
    const [state, dispatch] = useReducer(reducer, {
        succeeded: 0,
        failed: new Set([]),
        errors: []
    });
    useOnMount(()=>{
        async function sendSingleRequest(itemId) {
            try {
                if (canProcessItem(itemId)) {
                    const result = await processSingleItem(itemId);
                    if (result.success) {
                        dispatch({
                            type: 'succeeded'
                        });
                    } else {
                        dispatch({
                            type: 'failed',
                            failedId: itemId,
                            error: getErrorText ? getErrorText(itemId, result.error) : typeof result.error === 'string' && result.error || null
                        });
                    }
                } else {
                    dispatch({
                        type: 'succeeded'
                    });
                }
            } catch (err) {
                dispatch({
                    type: 'failed',
                    failedId: itemId,
                    error: null
                });
            }
        }
        bulkItemIds.reduce((asyncChain, itemId)=>asyncChain.then(()=>sendSingleRequest(itemId)), Promise.resolve());
    });
    const closeModal = ()=>dismissModal(Array.from(state.failed.values()));
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        title: progressTitle,
        textAlignCenter: false,
        width: "680px",
        content: /*#__PURE__*/ _jsxs("div", {
            children: [
                state.succeeded + state.failed.size < bulkItemIds.length ? /*#__PURE__*/ _jsxs(Typography, {
                    children: [
                        progressAction,
                        " ",
                        pluralize(itemName, bulkItemIds.length),
                        " (",
                        state.succeeded + state.failed.size,
                        "/",
                        bulkItemIds.length,
                        ")"
                    ]
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        state.succeeded > 0 && /*#__PURE__*/ _jsxs("div", {
                            className: styles.bulkRequestsResultSuccess,
                            children: [
                                /*#__PURE__*/ _jsx(Checkmark, {}),
                                ' ',
                                /*#__PURE__*/ _jsxs(Typography, {
                                    skin: "success",
                                    testId: "bulk-succeeded-text",
                                    children: [
                                        pluralize(itemName, state.succeeded, true),
                                        " ",
                                        actionName,
                                        " successfully"
                                    ]
                                })
                            ]
                        }),
                        state.failed.size > 0 && /*#__PURE__*/ _jsxs("div", {
                            className: styles.bulkRequestsResultFailure,
                            children: [
                                /*#__PURE__*/ _jsx(CloseCross, {}),
                                ' ',
                                /*#__PURE__*/ _jsxs(Typography, {
                                    skin: "error",
                                    testId: "bulk-failed-text",
                                    children: [
                                        pluralize(itemName, state.failed.size, true),
                                        " could not be ",
                                        actionName,
                                        errorExtra != null ? " ".concat(errorExtra) : ''
                                    ]
                                })
                            ]
                        }),
                        state.errors.length > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    style: {
                                        marginTop: '16px',
                                        marginBottom: '16px'
                                    },
                                    testId: "bulk-failed-details",
                                    children: "Details:"
                                }),
                                /*#__PURE__*/ _jsx("ul", {
                                    style: {
                                        marginLeft: '12px'
                                    },
                                    children: state.errors.map((error, index)=>/*#__PURE__*/ _jsx("li", {
                                            style: {
                                                listStyle: 'disc'
                                            },
                                            children: /*#__PURE__*/ _jsx(Typography, {
                                                children: error
                                            })
                                        }, index))
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: styles.progressBarBackground
                })
            ]
        }),
        primaryButtonLabel: commonTexts.buttons.close(),
        cancelButtonDisabled: bulkItemIds.length != state.succeeded + state.failed.size,
        primaryButtonDisabled: bulkItemIds.length != state.succeeded + state.failed.size,
        onPrimaryButtonClick: closeModal,
        onCloseButtonClick: closeModal
    });
}
export function useBulkActionModal() {
    const { t: commonTexts } = useLocaleKeys('common');
    const overlayContext = useOverlayContext();
    async function doBulkAction(props) {
        const { confirmation, onConfirmed, onDone } = props;
        const confirmed = await overlayContext.showModal({
            content: (p)=>confirmation.renderModal ? confirmation.renderModal(p) : /*#__PURE__*/ _jsx(ConfirmationModal, {
                    title: confirmation.title,
                    textAlignCenter: false,
                    content: confirmation.content,
                    primaryButtonLabel: confirmation.primaryButtonLabel,
                    secondaryButtonLabel: commonTexts.buttons.cancel(),
                    onPrimaryButtonClick: ()=>p.dismissModal(true),
                    onSecondaryButtonClick: ()=>p.dismissModal(false)
                })
        });
        if (!confirmed) {
            return;
        }
        onConfirmed && onConfirmed();
        const failedItemIds = await overlayContext.showModal({
            content: (p)=>/*#__PURE__*/ _jsx(BulkActionModal, _object_spread({}, props, p))
        });
        onDone(failedItemIds);
    }
    return {
        doBulkAction
    };
}
